import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { bulkUploadCustomers, bulkUploadSales, bulkUploadLeads, listAudit, undoImport } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, ShieldCheck, Users, ReceiptText,
  History, Undo2, RefreshCw, ClipboardList, Eye, X,
} from "lucide-react";

/**
 * Bulk Import — one-time historical data ingestion, scoped to the ACTIVE facility.
 * Two CSV importers, each with a downloadable template that matches its backend schema.
 */
export default function BulkImport() {
  const { activeFacility } = useAuth();
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const refreshHistory = async () => {
    setHistoryLoading(true);
    try {
      const rows = await listAudit({ entity_type: "import", limit: 50 });
      setHistoryRows(rows);
    } catch (err) {
      // silent — non-fatal
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { refreshHistory(); }, []);

  const handleImportComplete = () => {
    refreshHistory();
  };

  return (
    <div className="space-y-8" data-testid="bulk-import-page">
      <div>
        <div className="eyebrow flex items-center gap-2">
          <Upload className="h-3.5 w-3.5 text-amber-400" />
          One-time data migration
        </div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Bulk Import</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Import your existing customers and historical sales into
          <span className="text-amber-300 font-medium mx-1">{activeFacility?.name || "this facility"}</span>
          from CSV files. Everything is scoped to the current facility — switch facilities from the top-right to import into a different gym.
        </p>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="bulk-import-warning">
        <ShieldCheck className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-200/90">
          <div className="font-semibold text-amber-200 uppercase tracking-[0.15em] text-[10px]">Before you upload</div>
          <ul className="mt-1.5 space-y-1 list-disc list-inside">
            <li>Confirm the active facility above is the correct destination.</li>
            <li>Download the template, fill it in exactly, and save as CSV (UTF-8).</li>
            <li>Rows are upserted — re-running is safe. Sales dedup on phone + date + name; customers on phone.</li>
          </ul>
        </div>
      </div>

      <ImportCard
        title="Customers"
        subtitle="Every member you want to see in the roster — active packages, contact details."
        icon={Users}
        color="text-blue-300"
        border="border-blue-500/30"
        bg="bg-blue-500/5"
        templateHeaders={CUSTOMER_HEADERS}
        templateSample={CUSTOMER_SAMPLE}
        uploader={bulkUploadCustomers}
        testidPrefix="bulk-customers"
        requiredNote="Required: first_name, last_name, phone"
        onDone={handleImportComplete}
      />

      <ImportCard
        title="Sales"
        subtitle="Historical sales — pricing, programme, payment mode, dates, member manager. Customers are auto-populated from these rows too — no separate customers upload needed."
        icon={ReceiptText}
        color="text-emerald-300"
        border="border-emerald-500/30"
        bg="bg-emerald-500/5"
        templateHeaders={SALES_HEADERS}
        templateSample={SALES_SAMPLE}
        uploader={bulkUploadSales}
        testidPrefix="bulk-sales"
        requiredNote="Required: member_name, phone, paid, member_manager · Dedup: sales by phone+date+name; customers by phone (email fallback)"
        onDone={handleImportComplete}
      />

      <ImportCard
        title="Front End Sales"
        subtitle="Historical Front End Sales / leads — populates the FE dashboard & incentives. Does NOT create matching All Sales rows. Paid rows auto-seed a payment ledger entry so FE Conversion incentives stay accurate."
        icon={ClipboardList}
        color="text-amber-300"
        border="border-amber-500/30"
        bg="bg-amber-500/5"
        templateHeaders={LEADS_HEADERS}
        templateSample={LEADS_SAMPLE}
        uploader={bulkUploadLeads}
        testidPrefix="bulk-leads"
        requiredNote="Required: name, phone · Dedup: leads by phone+date+name. amount_paid>0 seeds a payment row automatically."
        onDone={handleImportComplete}
      />

      <ImportHistory rows={historyRows} loading={historyLoading} onRefresh={refreshHistory} />
    </div>
  );
}

function ImportHistory({ rows, loading, onRefresh }) {
  const doUndo = async (row) => {
    if (!window.confirm(`Undo this import — "${row.filename}"?\n\nThis will DELETE ${row.created_ids?.length || 0} row(s) that this import created. Rows that were only updated will be left alone.`)) return;
    try {
      const res = await undoImport(row.id);
      toast.success(`Undo complete · ${res.deleted} row(s) removed`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    }
  };

  return (
    <section className="rounded-xl border border-[#262626] bg-[#0d0d0d] p-5" data-testid="bulk-import-history">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-amber-400" />
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-300">Import history</div>
          <span className="text-[11px] text-zinc-500">({rows.length})</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414] text-xs" data-testid="bulk-import-history-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 && !loading && (
        <div className="text-xs text-zinc-500 py-4 text-center">No imports yet.</div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Who</th>
                <th className="px-3 py-2 font-medium">Kind</th>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium">Created</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium">Errors</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r) => {
                const kind = (r.note === "sales_bulk_upload") ? "Sales" : (r.note === "customers_bulk_upload") ? "Customers" : (r.note === "leads_bulk_upload") ? "Front End Sales" : (r.action === "import_undo" ? "Undo" : "Import");
                const c = r.import_counts || {};
                const isUndone = !!r.undone_at;
                const isUndoAction = r.action === "import_undo";
                const canUndo = !isUndoAction && !isUndone && (r.created_ids?.length || 0) > 0;
                return (
                  <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0" data-testid={`bulk-import-history-row-${r.id}`}>
                    <td className="px-3 py-2 text-zinc-400 font-mono whitespace-nowrap">{new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-3 py-2">
                      <div className="text-zinc-200">{r.actor_name || "—"}</div>
                      <div className="text-[10px] text-zinc-500">{r.actor_email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`border ${kind === "Sales" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : kind === "Customers" ? "bg-blue-500/10 text-blue-300 border-blue-500/30" : kind === "Front End Sales" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-zinc-500/10 text-zinc-300 border-zinc-500/30"}`}>{kind}</Badge>
                    </td>
                    <td className="px-3 py-2 text-zinc-300 font-mono">{r.filename || (isUndoAction ? "—" : "")}</td>
                    <td className="px-3 py-2 text-emerald-300">{c.created ?? "—"}</td>
                    <td className="px-3 py-2 text-blue-300">{c.updated ?? "—"}</td>
                    <td className={`px-3 py-2 ${(c.errors || 0) > 0 ? "text-rose-300" : "text-zinc-500"}`}>{c.errors ?? "—"}</td>
                    <td className="px-3 py-2">
                      {isUndoAction ? (
                        <Badge className="border bg-zinc-500/10 text-zinc-300 border-zinc-500/30">reversed an import</Badge>
                      ) : isUndone ? (
                        <Badge className="border bg-rose-500/10 text-rose-300 border-rose-500/30">Undone</Badge>
                      ) : (
                        <Badge className="border bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Applied</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canUndo ? (
                        <button
                          onClick={() => doUndo(r)}
                          className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                          data-testid={`bulk-import-undo-${r.id}`}
                        >
                          <Undo2 className="h-3 w-3" /> Undo
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-700">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ImportCard({
  title, subtitle, icon: Icon, color, border, bg,
  templateHeaders, templateSample, uploader, testidPrefix, requiredNote, onDone,
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result
  const [result, setResult] = useState(null);   // final commit result

  const downloadTemplate = () => {
    const csv = [templateHeaders.join(","), templateSample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase()}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const runPreview = async () => {
    if (!file) return toast.error(`Choose a ${title.toLowerCase()} CSV first`);
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const res = await uploader(file, true);
      setPreview(res);
      toast.success(`Preview ready · ${res.created} to insert · ${res.updated} to update${res.errors ? ` · ${res.errors} error(s)` : ""}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Preview failed";
      toast.error(msg);
      setPreview({ errors: 1, error_details: [{ row: "—", error: msg }], preview: [] });
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!file) return toast.error(`Choose a ${title.toLowerCase()} CSV first`);
    setBusy(true);
    setResult(null);
    try {
      const res = await uploader(file, false);
      setResult(res);
      setPreview(null);
      const custMsg = (res.customers_created || res.customers_updated) ? ` · customers +${res.customers_created || 0}/~${res.customers_updated || 0}` : "";
      toast.success(`${title} import complete: ${res.created} created · ${res.updated} updated${res.errors ? ` · ${res.errors} errors` : ""}${custMsg}`);
      if (onDone) onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(msg);
      setResult({ errors: 1, error_details: [{ row: "—", error: msg }] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`rounded-xl border ${border} ${bg} p-5`} data-testid={`${testidPrefix}-card`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className={`inline-flex items-center gap-2 ${color}`}>
            <Icon className="h-4 w-4" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold">{title}</div>
          </div>
          <h3 className="font-display text-xl font-bold mt-1">{title} import</h3>
          <p className="text-xs text-zinc-400 mt-1 max-w-lg">{subtitle}</p>
          <div className="text-[11px] text-zinc-500 mt-2 font-mono">{requiredNote}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414] text-xs"
            data-testid={`${testidPrefix}-download-template`}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Template
          </Button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setPreview(null); }}
          className="hidden"
          data-testid={`${testidPrefix}-file-input`}
        />
        <Button
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414]"
          data-testid={`${testidPrefix}-pick-file`}
        >
          <FileSpreadsheet className="h-4 w-4 mr-1.5" />
          {file ? "Change file" : "Choose CSV"}
        </Button>
        <div className="text-xs text-zinc-400 min-h-[1rem]">
          {file ? (
            <span><span className="text-zinc-300 font-medium">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB</span>
          ) : (
            <span className="text-zinc-600">No file chosen yet</span>
          )}
        </div>
        <div className="grow" />
        <Button
          variant="outline"
          disabled={previewing || busy || !file}
          onClick={runPreview}
          className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414] text-zinc-200"
          data-testid={`${testidPrefix}-preview`}
        >
          <Eye className="h-4 w-4 mr-1.5" />
          {previewing ? "Checking…" : "Preview"}
        </Button>
        <Button
          disabled={busy || previewing || !file}
          onClick={submit}
          className={`rounded-full text-black ${title === "Customers" ? "bg-blue-500 hover:bg-blue-400" : title === "Sales" ? "bg-emerald-500 hover:bg-emerald-400" : "bg-amber-500 hover:bg-amber-400"}`}
          data-testid={`${testidPrefix}-submit`}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          {busy ? "Uploading…" : preview ? `Confirm & Import ${title.toLowerCase()}` : `Import ${title.toLowerCase()}`}
        </Button>
      </div>

      {preview && (
        <PreviewPanel preview={preview} testidPrefix={testidPrefix} title={title} onDismiss={() => setPreview(null)} />
      )}

      {result && (
        <div className="mt-4 grid grid-cols-3 gap-2 text-xs" data-testid={`${testidPrefix}-result`}>
          <ResultTile label="Created"  value={result.created ?? 0} tone="emerald" />
          <ResultTile label="Updated"  value={result.updated ?? 0} tone="blue" />
          <ResultTile label="Errors"   value={result.errors ?? 0}  tone={(result.errors || 0) > 0 ? "rose" : "zinc"} />
          {(result.customers_created !== undefined || result.customers_updated !== undefined) && (
            <div className="col-span-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 text-[11px] text-cyan-200" data-testid={`${testidPrefix}-customer-tally`}>
              Customers upserted alongside the sales · <span className="font-mono text-cyan-100">{result.customers_created || 0} new</span> · <span className="font-mono text-cyan-100">{result.customers_updated || 0} updated</span>
            </div>
          )}
          {result.payments_seeded !== undefined && (
            <div className="col-span-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200" data-testid={`${testidPrefix}-payments-tally`}>
              Payment ledger entries auto-seeded · <span className="font-mono text-amber-100">{result.payments_seeded || 0} row(s)</span> — used by FE Conversion incentives.
            </div>
          )}
          {(result.error_details || []).length > 0 && (
            <div className="col-span-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 mt-1" data-testid={`${testidPrefix}-error-details`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-semibold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> First {result.error_details.length} error(s)
              </div>
              <ul className="space-y-1 text-[11px] text-rose-100/90 max-h-40 overflow-y-auto">
                {(result.error_details || []).map((e) => (
                  <li key={`err-${e.row}-${e.error}`}><span className="font-mono text-rose-300 mr-2">row {e.row}</span>{e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <details data-testid="bulk-import-details-380" className="mt-4 group">
        <summary data-testid="bulk-import-summary-381" className="cursor-pointer text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300">CSV column reference ({templateHeaders.length} columns)</summary>
        <div className="mt-2 rounded-lg border border-[#262626] bg-[#0a0a0a] p-3 overflow-x-auto">
          <table className="text-[11px] font-mono">
            <tbody>
              <tr>
                {(templateHeaders || []).map((h) => (
                  <td key={h} className="px-2 py-1 text-zinc-400 border-r border-[#1f1f1f] last:border-0 whitespace-nowrap">{h}</td>
                ))}
              </tr>
              <tr>
                {(templateSample || []).map((v, i) => (
                  <td key={`${templateHeaders[i] || "col"}-${i}`} className="px-2 py-1 text-zinc-600 border-r border-[#1f1f1f] last:border-0 whitespace-nowrap">{v || <span className="text-zinc-800">—</span>}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function PreviewPanel({ preview, testidPrefix, title, onDismiss }) {
  const rows = preview.preview || [];
  const c = preview.created || 0;
  const u = preview.updated || 0;
  const e = preview.errors || 0;
  const hasErrors = e > 0;
  const columns = rows[0] ? Object.keys(rows[0]).filter((k) => k !== "action") : [];
  return (
    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`${testidPrefix}-preview-panel`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-amber-300" />
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-300">Dry-run preview · nothing saved yet</div>
        </div>
        <button onClick={onDismiss} className="text-zinc-500 hover:text-zinc-300" data-testid={`${testidPrefix}-preview-dismiss`}><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Will insert</div>
          <div className="font-display text-2xl font-extrabold text-emerald-200 mt-1">{c}</div>
        </div>
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-2.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-semibold">Will update</div>
          <div className="font-display text-2xl font-extrabold text-blue-200 mt-1">{u}</div>
        </div>
        <div className={`rounded-lg border p-2.5 ${hasErrors ? "border-rose-500/30 bg-rose-500/5" : "border-[#262626] bg-[#0a0a0a]"}`}>
          <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${hasErrors ? "text-rose-300" : "text-zinc-400"}`}>Will skip (errors)</div>
          <div className={`font-display text-2xl font-extrabold mt-1 ${hasErrors ? "text-rose-200" : "text-zinc-300"}`}>{e}</div>
        </div>
      </div>
      {(preview.customers_created !== undefined || preview.customers_updated !== undefined) && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2.5 text-[11px] text-cyan-200 mb-3">
          Customers would be upserted alongside · <span className="font-mono text-cyan-100">{preview.customers_created || 0} new</span> · <span className="font-mono text-cyan-100">{preview.customers_updated || 0} updated</span>
        </div>
      )}
      {preview.payments_seeded !== undefined && preview.payments_seeded > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-200 mb-3">
          <span className="font-mono text-amber-100">{preview.payments_seeded}</span> payment ledger row(s) would auto-seed for rows with amount_paid &gt; 0.
        </div>
      )}
      {(preview.error_details || []).length > 0 && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-semibold mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Errors ({preview.error_details.length} shown)
          </div>
          <ul className="space-y-1 text-[11px] text-rose-100/90 max-h-32 overflow-y-auto">
            {(preview.error_details || []).map((err) => (
              <li key={`preview-err-${err.row}-${err.error}`}><span className="font-mono text-rose-300 mr-2">row {err.row}</span>{err.error}</li>
            ))}
          </ul>
        </div>
      )}
      {rows.length > 0 && (
        <div className="rounded-lg border border-[#262626] bg-[#0a0a0a] overflow-x-auto max-h-72 overflow-y-auto" data-testid={`${testidPrefix}-preview-table`}>
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-[#0a0a0a] z-10">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-2 py-1.5 font-medium">Row</th>
                <th className="px-2 py-1.5 font-medium">Action</th>
                {(columns || []).filter((k) => k !== "row").map((k) => (
                  <th key={k} className="px-2 py-1.5 font-medium">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.row} className="border-b border-[#1a1a1a] last:border-0" data-testid={`${testidPrefix}-preview-row-${r.row}`}>
                  <td className="px-2 py-1.5 font-mono text-zinc-500">{r.row}</td>
                  <td className="px-2 py-1.5">
                    <Badge className={`border ${r.action === "insert" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-blue-500/10 text-blue-300 border-blue-500/30"}`}>{r.action}</Badge>
                  </td>
                  {(columns || []).filter((k) => k !== "row").map((k) => (
                    <td key={k} className="px-2 py-1.5 text-zinc-300 font-mono whitespace-nowrap">
                      {typeof r[k] === "boolean" ? (r[k] ? "✓" : "—") : (r[k] === "" || r[k] == null ? <span className="text-zinc-700">—</span> : String(r[k]))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 100 && (
            <div className="text-[10px] text-zinc-500 text-center py-2 border-t border-[#1f1f1f]">Showing first 100 rows · full totals in the tiles above.</div>
          )}
        </div>
      )}
      <div className="text-[11px] text-zinc-400 mt-3">
        Review the preview above, then click <span className="text-amber-200 font-medium">Confirm &amp; Import {title.toLowerCase()}</span> to commit.
      </div>
    </div>
  );
}

function ResultTile({ label, value, tone }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
    blue:    "border-blue-500/30 bg-blue-500/5 text-blue-200",
    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-200",
    zinc:    "border-[#262626] bg-[#0a0a0a] text-zinc-300",
  };
  const Icon = tone === "rose" ? AlertTriangle : CheckCircle2;
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

/* ---------------- CSV templates ---------------- */
const CUSTOMER_HEADERS = [
  "first_name", "last_name", "email", "phone",
  "package", "start_date", "end_date",
];
const CUSTOMER_SAMPLE = [
  "Priya", "Sharma", "priya@example.com", "9876543210",
  "12 Week", "01-11-2025", "24-01-2026",
];

const SALES_HEADERS = [
  "member_name", "phone", "email", "date", "programme", "fe_pt_pe", "status",
  "paid", "payment_mode", "payment_received_by",
  "member_manager", "accountability_coach", "lead_nurture_exec",
  "start_date", "end_date", "blood_test", "remarks",
];
const SALES_SAMPLE = [
  "Priya Sharma", "9876543210", "priya@example.com", "01-11-2025", "12 Week", "FE", "New",
  "15000", "UPI - POS", "Rohit",
  "Neha", "Karthik", "Sana",
  "01-11-2025", "24-01-2026", "false", "Migrated from legacy sheet",
];

const LEADS_HEADERS = [
  "name", "phone", "email", "date",
  "sales_ex", "lead_ex", "programme", "lead_status",
  "amount_sold", "amount_paid", "mode_of_payment", "refund_amount",
  "programme_started", "programme_started_at",
  "start_date", "end_date", "next_payment_due_date", "remarks",
];
const LEADS_SAMPLE = [
  "Priya Sharma", "9876543210", "priya@example.com", "01-11-2025",
  "Rohit", "Sana", "12 Week", "sold",
  "20000", "15000", "UPI - POS", "0",
  "true", "05-11-2025",
  "05-11-2025", "28-01-2026", "", "Migrated from legacy FE sheet",
];
